import React, { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth-context";
import { formatApiErrorDetail } from "../lib/api";
import { FormPrivacyNote } from "../components/FormPrivacyNote";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
  const home = (u) => (u.role === "admin" ? (safeNext?.startsWith("/admin") ? safeNext : "/admin/patients") : safeNext || "/dashboard");
  const [form, setForm] = useState({ email: "", password: "" });
  const carried = useLocation().state || {};
  const [step, setStep] = useState(carried.mfaToken ? "mfa" : "password"); // 'password' | 'mfa'
  const [mfaToken, setMfaToken] = useState(carried.mfaToken || "");
  const [code, setCode] = useState("");
  const [rememberAllowed, setRememberAllowed] = useState(!!carried.rememberAllowed);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(form.email, form.password);
      if (res.mfaRequired) {
        setMfaToken(res.mfaToken);
        setRememberAllowed(res.rememberAllowed);
        setStep("mfa");
      } else {
        const u = res.user;
        navigate(home(u));
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await verifyMfa(mfaToken, code, rememberAllowed && remember);
      navigate(home(u));
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errCode = (typeof detail === "object" && detail) ? detail.code : null;
      if (errCode === "mfa_token_expired" || errCode === "mfa_token_invalid") {
        setStep("password");
        setMfaToken("");
        setCode("");
        setError(detail.message || "Your sign-in session expired. Please log in again.");
      } else {
        setError(formatApiErrorDetail(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full rounded-sm border border-stone-300 bg-white px-4 py-3 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-bronze-dark/40 focus:border-bronze-dark";

  if (step === "mfa") {
    return (
      <div data-testid="login-mfa-page" className="container-ccc py-16 max-w-md">
        <div className="eyebrow">Two-factor authentication</div>
        <h1 className="font-serif text-4xl mt-3">Enter your code.</h1>
        <p className="mt-3 text-ink-muted text-sm">
          Open your authenticator app (Google Authenticator, 1Password, Bitwarden) and enter the 6-digit code. You can also paste a backup code if you've lost your phone.
        </p>
        <form onSubmit={submitMfa} data-testid="login-mfa-form" className="mt-10 space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">Authentication code</label>
            <input required autoFocus type="text" inputMode="text" autoComplete="one-time-code"
              data-testid="login-mfa-code-input"
              className={input} value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="123456 or XXXX-XXXX-XXXX" />
          </div>
          {rememberAllowed && (
            <label className="flex items-start gap-3 text-sm text-ink cursor-pointer select-none">
              <input type="checkbox" data-testid="login-mfa-remember-checkbox"
                className="mt-0.5 h-4 w-4 accent-[#8a6a3b]"
                checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              <span>
                Remember this browser for 30 days
                <span className="block text-xs text-ink-muted mt-0.5">Only tick this on a private computer you trust.</span>
              </span>
            </label>
          )}
          {error && <div data-testid="login-mfa-error" className="text-sm text-destructive">{error}</div>}
          <button disabled={loading} type="submit" data-testid="login-mfa-submit-button" className="btn-primary w-full">
            {loading ? "Verifying…" : "Verify & sign in"}
          </button>
          <button type="button" onClick={() => { setStep("password"); setCode(""); setError(""); }}
            className="text-sm text-bronze-dark hover:text-bronze">
            ← Cancel
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="login-page" className="container-ccc py-16 max-w-md">
      <div className="eyebrow">Patient portal</div>
      <h1 className="font-serif text-4xl mt-3">Welcome back.</h1>
      <p className="mt-3 text-ink-muted text-sm">Log in to manage your enquiries and appointments.</p>
      <form onSubmit={submitPassword} data-testid="login-form" className="mt-10 space-y-5">
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">Email</label>
          <input required type="email" data-testid="login-email-input" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">Password</label>
          <input required type="password" data-testid="login-password-input" className={input} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        {error && <div data-testid="login-error" className="text-sm text-destructive">{error}</div>}
        <button disabled={loading} type="submit" data-testid="login-submit-button" className="btn-primary w-full">{loading ? "Signing in…" : "Continue"}</button>
        <FormPrivacyNote />
      </form>
      <div className="mt-6 flex justify-between text-sm">
        <Link to="/forgot-password" data-testid="login-forgot-link" className="text-bronze-dark">Forgot password?</Link>
        <Link to="/register" data-testid="login-register-link" className="text-bronze-dark">Create account</Link>
      </div>
    </div>
  );
}
